"use client"

import { useEffect, useRef } from "react"

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: TurnstileOptions) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
  }
}

interface TurnstileOptions {
  sitekey: string
  callback: (token: string) => void
  "expired-callback": () => void
  "error-callback": () => void
  theme?: "light" | "dark" | "auto"
  size?: "normal" | "compact"
}

interface Props {
  onVerify: (token: string) => void
  onExpire?: () => void
  theme?: "light" | "dark" | "auto"
}

const SCRIPT_ID = "cf-turnstile-script"
const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? ""

export function TurnstileWidget({ onVerify, onExpire, theme = "auto" }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!SITE_KEY) {
      console.error(
        "[TurnstileWidget] NEXT_PUBLIC_TURNSTILE_SITE_KEY absente du bundle — vérifier les build-args Docker"
      )
      return
    }

    function renderWidget() {
      if (!containerRef.current || !window.turnstile) return
      if (widgetIdRef.current) return
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: onVerify,
        "expired-callback": () => {
          widgetIdRef.current = null
          onExpire?.()
        },
        "error-callback": () => {
          widgetIdRef.current = null
          onExpire?.()
        },
        theme,
        size: "normal",
      })
    }

    if (window.turnstile) {
      renderWidget()
      return
    }

    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement("script")
      script.id = SCRIPT_ID
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js"
      script.async = true
      script.defer = true
      script.onload = renderWidget
      document.head.appendChild(script)
    } else {
      const interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval)
          renderWidget()
        }
      }, 100)
      return () => clearInterval(interval)
    }
  }, [onVerify, onExpire, theme])

  useEffect(() => {
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [])

  if (!SITE_KEY) {
    return (
      <p style={{ fontSize: 13, color: "#DC2626", textAlign: "center", margin: 0 }}>
        Vérification anti-robot indisponible — veuillez nous contacter par téléphone.
      </p>
    )
  }

  return <div ref={containerRef} />
}
